// Post to Many — the per-account preview LIST (Scott, 2026-10-01: a list, not
// a stepper; the failure being checked for is ONE account rendering oddly, and
// a list lets him scan). Pure: every rule here is asserted without jsdom.
//
// This is the payoff of caption wildcards: one caption is only safe across
// twenty accounts because the account-specific bits resolve per account, and
// this is where somebody can SEE that before confirming.
//
// ⚠️ Resolution goes through resolveCaptionWildcards and the contexts come from
// the ONE server builder (wildcardContexts.ts) — never a second resolver, or
// the preview and the dispatch could disagree about what publishes.
// ⚠️ Coverage is the AccountCoverage buildCoveragePlan already computed; it is
// passed in, never recomputed.
// ⚠️ An account whose context did not load is shown as FAILED — never a caption
// with raw {tokens} left in it, and never a silently missing row. A missing row
// in a list whose whole job is completeness is the one outcome worse than an
// error.

import {
  CAPTION_WILDCARDS,
  LEGACY_WILDCARD_ALIASES,
  resolveCaptionWildcards,
  type WildcardContext,
} from './captionWildcards';
import { destinationById, type PostDestinationId } from './destinations';
import type { AccountCoverage } from './multiAccount';

export type ContextsLoad =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ok'; contexts: Record<string, WildcardContext | null> };

export interface PreviewCaption {
  /** "Main caption" or the destination's label when it has its own caption. */
  label: string;
  text: string;
  /** Labels of merge fields this caption uses that are EMPTY for this account. */
  blankFields: string[];
}

export type PerAccountPreviewEntry =
  | { accountId: string; name: string; coverage: AccountCoverage; state: 'loading' }
  | { accountId: string; name: string; coverage: AccountCoverage; state: 'failed'; reason: string }
  | {
      accountId: string;
      name: string;
      coverage: AccountCoverage;
      state: 'ok';
      captions: PreviewCaption[];
      /** Why there are no captions, when there are none (stories only / receives nothing). */
      note: string | null;
    };

export interface PerAccountPreviewInput {
  /** The coverage plan, one row per SELECTED account, in display order. */
  coverage: AccountCoverage[];
  load: ContextsLoad;
  /** The authored main caption (tokens intact). */
  baseCaption: string;
  /** Destinations whose own (authored) caption is in effect — see captionOverride.ts. */
  ownCaptions: Array<{ destination: PostDestinationId; text: string }>;
}

const FAILED_ACCOUNT = "Couldn't load this account's details, so its caption can't be previewed.";

/** Label every KNOWN token in `authored` whose value is '' for this account. */
export function blankFieldsIn(authored: string, ctx: WildcardContext): string[] {
  const out: string[] = [];
  for (const def of CAPTION_WILDCARDS) {
    if (authored.includes(`{${def.token}}`) && def.resolve(ctx).trim() === '') out.push(def.label);
  }
  for (const [token, resolve] of Object.entries(LEGACY_WILDCARD_ALIASES)) {
    if (authored.includes(`{${token}}`) && resolve(ctx).trim() === '') out.push(`{${token}}`);
  }
  return [...new Set(out)];
}

export function buildPerAccountPreview(input: PerAccountPreviewInput): PerAccountPreviewEntry[] {
  const ownById = new Map(input.ownCaptions.map((c) => [c.destination, c.text]));
  return input.coverage.map((coverage) => {
    const base = { accountId: coverage.account.id, name: coverage.account.name, coverage };
    if (input.load.status === 'loading') return { ...base, state: 'loading' as const };
    if (input.load.status === 'error') {
      return { ...base, state: 'failed' as const, reason: input.load.message };
    }
    const ctx = input.load.contexts[coverage.account.id];
    if (!ctx) return { ...base, state: 'failed' as const, reason: FAILED_ACCOUNT };

    // Only destinations this account will actually post to, and never a story
    // (stories carry no caption).
    const captioned = coverage.covered.filter((d) => destinationById(d).placement !== 'story');
    const captions: PreviewCaption[] = [];
    if (captioned.some((d) => !ownById.has(d))) {
      captions.push({
        label: 'Main caption',
        text: resolveCaptionWildcards(input.baseCaption, ctx),
        blankFields: blankFieldsIn(input.baseCaption, ctx),
      });
    }
    for (const d of captioned) {
      const own = ownById.get(d);
      if (own === undefined) continue;
      captions.push({
        label: destinationById(d).label,
        text: resolveCaptionWildcards(own, ctx),
        blankFields: blankFieldsIn(own, ctx),
      });
    }
    const note = !coverage.hasCoverage
      ? 'Receives nothing: none of the ticked destinations are connected for this account.'
      : captions.length === 0
        ? 'Stories only, which carry no caption.'
        : null;
    return { ...base, state: 'ok' as const, captions, note };
  });
}

// ---------------------------------------------------------------------------
// Preview dialog (customise brief, "Preview" — built before Customise).
// ---------------------------------------------------------------------------

/**
 * Whether a row's Preview can open. Only an account whose context loaded and
 * that receives at least one destination has a post to preview; a failed row
 * already says why, and a zero-coverage account publishes nothing.
 */
export function canPreviewEntry(entry: PerAccountPreviewEntry): boolean {
  return entry.state === 'ok' && entry.coverage.hasCoverage;
}

/** The dialog's tabs: the destinations this dealer actually posts to. */
export function previewTabs(coverage: AccountCoverage): PostDestinationId[] {
  return [...coverage.covered];
}

/**
 * The shared composition's targets narrowed to ONE destination for ONE dealer:
 * only `destination` stays enabled, and only if this dealer covers it. Never
 * mutates the shared targets — a preview must not be able to write into the
 * composition every other dealer reads from.
 */
export function previewTargetsFor<T extends { enabled: boolean }>(
  targets: Record<PostDestinationId, T>,
  coverage: AccountCoverage,
  destination: PostDestinationId,
): Record<PostDestinationId, T> {
  const covered = new Set(coverage.covered);
  const out = {} as Record<PostDestinationId, T>;
  for (const id of Object.keys(targets) as PostDestinationId[]) {
    const t = targets[id];
    out[id] = { ...t, enabled: t.enabled && id === destination && covered.has(id) };
  }
  return out;
}
